import React, { useState, useEffect } from 'react';
import { X, FileText, Printer, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { getOfferTemplate, OfferTemplateType } from '../api';

interface Placeholder { key: string; label: string; sample: string }

interface Props {
  /** 预填值：key 对应模版占位符，如 candidate_name_cn / position_cn 等 */
  initial?: Record<string, string>;
  /** 默认模版类型（按用工类型推断：实习→intern，全职→formal_no_equity） */
  defaultType?: OfferTemplateType;
  onClose: () => void;
}

/**
 * 生成 Offer 录用通知书附件：
 * 读取「系统设置 → Offer 信推模版」，用表单里的自定义信息替换 {{占位符}}，
 * 在新标签打开正式排版页面，由浏览器「打印 / 保存为 PDF」导出。
 */
export default function OfferLetterModal({ initial, defaultType = 'intern', onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState('');
  const [placeholders, setPlaceholders] = useState<Placeholder[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [tplType, setTplType] = useState<OfferTemplateType>(defaultType);
  const [tplTypes, setTplTypes] = useState<{ key: OfferTemplateType; name: string }[]>([]);

  const loadType = (type: OfferTemplateType, isInitial = false) => {
    if (isInitial) setLoading(true);
    getOfferTemplate(type)
      .then(d => {
        setTplType(d.type);
        setTemplate(d.content || '');
        const phs = d.placeholders || [];
        setPlaceholders(phs);
        if (d.types) setTplTypes(d.types);
        setValues(prev => {
          const next: Record<string, string> = {};
          phs.forEach(p => { next[p.key] = prev[p.key] ?? (initial && initial[p.key]) ?? ''; });
          return next;
        });
      })
      .catch(() => toast.error('加载 Offer 模版失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadType(defaultType, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k: string, v: string) => setValues(prev => ({ ...prev, [k]: v }));

  const generate = () => {
    const filled = template.replace(/{{\s*([\w]+)\s*}}/g, (_m, k) => values[k] ?? '');
    const w = window.open('', '_blank');
    if (!w) { toast.error('浏览器拦截了新窗口，请允许弹出'); return; }
    w.document.open();
    w.document.write(filled);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800 flex items-center gap-2">
            <FileText size={18} className="text-primary-600" /> 生成 Offer 附件
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-16 justify-center">
            <RefreshCw size={16} className="animate-spin" /> 加载模版中...
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {tplTypes.length > 1 && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">模版类型</label>
                  <div className="flex items-center gap-2">
                    {tplTypes.map(t => (
                      <button
                        key={t.key}
                        onClick={() => loadType(t.key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${tplType === t.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 bg-primary-50 rounded-lg px-3 py-2">
                填写下列信息后点「生成并打印」，将在新标签打开正式排版的录用通知书，用浏览器「打印 / 保存为 PDF」即可导出附件。模版可在<span className="font-medium text-primary-700">系统设置 → Offer 信推模版</span>中修改。
              </p>
              {placeholders.map(p => (
                <div key={p.key}>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{p.label}</label>
                  <input
                    value={values[p.key] || ''}
                    onChange={e => set(p.key, e.target.value)}
                    placeholder={p.sample}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>
              ))}
            </div>
            <div className="px-5 py-3 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">取消</button>
              <button onClick={generate} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg">
                <Printer size={15} /> 生成并打印
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
